import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  register: async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    return response.data;
  },
  
  verifyToken: async (token) => {
    const response = await api.get('/auth/verify', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// QR Code API
export const qrAPI = {
  create: async (qrData) => {
    const response = await api.post('/qr/create', qrData);
    return response.data;
  },
  
  update: async (id, qrData) => {
    const response = await api.put(`/qr/${id}`, qrData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/qr/${id}`);
    return response.data;
  },
  
  getAll: async (workspaceId) => {
    const response = await api.get(`/qr?workspace=${workspaceId}`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/qr/${id}`);
    return response.data;
  }
};

// Analytics API
export const analyticsAPI = {
  getScans: async (qrId, timeRange = '7d') => {
    const response = await api.get(`/analytics/scans/${qrId}?range=${timeRange}`);
    return response.data;
  },
  
  getWorkspaceAnalytics: async (workspaceId, timeRange = '7d') => {
    const response = await api.get(`/analytics/workspace/${workspaceId}?range=${timeRange}`);
    return response.data;
  },
  
  exportData: async (workspaceId, format = 'csv') => {
    const response = await api.get(`/analytics/export/${workspaceId}?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// Workspace API
export const workspaceAPI = {
  getWorkspaces: async () => {
    const response = await api.get('/workspaces');
    return response.data;
  },
  
  createWorkspace: async (name) => {
    const response = await api.post('/workspaces', { name });
    return response.data;
  },
  
  updateWorkspace: async (id, data) => {
    const response = await api.put(`/workspaces/${id}`, data);
    return response.data;
  },
  
  deleteWorkspace: async (id) => {
    const response = await api.delete(`/workspaces/${id}`);
    return response.data;
  }
};

// Team API
export const teamAPI = {
  getMembers: async (workspaceId) => {
    const response = await api.get(`/team/${workspaceId}`);
    return response.data;
  },
  
  inviteMember: async (workspaceId, email, role) => {
    const response = await api.post(`/team/${workspaceId}/invite`, { email, role });
    return response.data;
  },
  
  updateMember: async (workspaceId, memberId, role) => {
    const response = await api.put(`/team/${workspaceId}/${memberId}`, { role });
    return response.data;
  },
  
  removeMember: async (workspaceId, memberId) => {
    const response = await api.delete(`/team/${workspaceId}/${memberId}`);
    return response.data;
  }
};

// Domain API
export const domainAPI = {
  getDomains: async (workspaceId) => {
    const response = await api.get(`/domains/${workspaceId}`);
    return response.data;
  },
  
  addDomain: async (workspaceId, domain) => {
    const response = await api.post(`/domains/${workspaceId}`, { domain });
    return response.data;
  },
  
  verifyDomain: async (workspaceId, domainId) => {
    const response = await api.post(`/domains/${workspaceId}/${domainId}/verify`);
    return response.data;
  },
  
  deleteDomain: async (workspaceId, domainId) => {
    const response = await api.delete(`/domains/${workspaceId}/${domainId}`);
    return response.data;
  }
};

// File API
export const fileAPI = {
  getFiles: async (workspaceId) => {
    const response = await api.get(`/files/${workspaceId}`);
    return response.data;
  },
  
  uploadFile: async (workspaceId, file, folder = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    const response = await api.post(`/files/${workspaceId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  deleteFile: async (workspaceId, fileId) => {
    const response = await api.delete(`/files/${workspaceId}/${fileId}`);
    return response.data;
  }
};

export default api;